import { useCallback, useEffect, useState } from 'react';
import {
  Divider,
  Stack,
  DialogContent,
  DialogActions,
  Dialog,
  Button,
  MenuItem,
  Typography,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch } from 'react-hook-form';
import { get } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { useTheme } from '@mui/material/styles';

import { Strings } from '@app/constants';
import { ILeave, ILeaveSchema, IPeriods } from '../types';
import { Header } from '@app/sections/header';
import {
  FormProvider,
  RHFTextField,
  RHFFilePickerField,
  Scrollbar,
  RHFDatePicker,
  Iconify,
} from '@app/components';
import { addLeaveSchema } from '../validator';
import { sendLeave, updateLeave, addLeave } from '../slice';
import { useDialog, useRequestState } from '@app/hooks';
import {
  selectAddLeaveState,
  selectLeaveState,
  selectSendLeaveState,
  selectUpdateLeaveState,
} from '../selectors';
import { selectLeaveManagementState, getLeaveManagement } from '@app/modules/leave-setting';
import { SelectPeriod } from './select-period';
import { InformationDialog } from './informative-dialog';
import { isWeekend } from '@app/utils';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: ILeave;
}
const AddLeave = ({ open, onClose: handleOnCloseLeavePopup, row }: Props) => {
  const dispatch = useDispatch();
  const notificationDailog = useDialog();
  const [fileError, setFileError] = useState<string | null>(null);

  const methods = useForm<ILeaveSchema>({
    resolver: yupResolver(addLeaveSchema) as any,
    defaultValues: {
      leaveType: get(row, 'leaveType', ''),
      from: get(row, 'from', ''),
      to: get(row, 'to', ''),
      reason: get(row, 'reason', ''),
      attachment: '',
      periods: row?.periods,
    },
  });

  const [periods, setPeriods] = useState<IPeriods[]>([]);

  const [selectedDates, setSelectedDates] = useState<{ from: Dayjs | null; to: Dayjs | null }>({
    from: dayjs(row?.from, 'DD/MM/YYYY'),
    to: dayjs(row?.to, 'DD/MM/YYYY'),
  });

  const handleSelectedFromDate = (date: Dayjs) => {
    setSelectedDates((prevState) => ({
      ...prevState,
      from: date,
    }));
  };

  const handleSelectedToDate = (date: Dayjs) => {
    setSelectedDates((prevState) => ({
      ...prevState,
      to: date,
    }));
  };

  const handleShowNotification = useCallback(() => {
    notificationDailog.show();
  }, []);

  const saveInDraftSuccess = useCallback(() => {
    handleOnCloseLeavePopup();
    handleShowNotification();
  }, []);

  const { loading } = useRequestState({
    stateSelector: row ? selectUpdateLeaveState : selectAddLeaveState,
    onSuccess: saveInDraftSuccess,
    successMessageShown: true,
    errorShown: true,
  });

  const handleCloseNotification = useCallback(() => {
    notificationDailog.hide();
  }, []);

  const { loading: sendLeaves } = useRequestState({
    stateSelector: selectSendLeaveState,
    onSuccess: handleOnCloseLeavePopup,
    successMessageShown: true,
    errorShown: true,
  });

  const { control } = methods;
  const file = useWatch({ control, name: 'attachment' });

  const validateFile = (file: any) => {
    if (!file) return true;

    const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setFileError('Only PDF, PNG, and JPG files are allowed');
      return false;
    }
    setFileError(null);
    return true;
  };

  const onSubmitDraft = (form: ILeaveSchema) => {
    if (!validateFile(file)) return;
    row
      ? dispatch(updateLeave({ ...form, periods, id: row?.id }))
      : dispatch(addLeave({ ...form, periods, attachment: file }));
  };

  const onSubmitSend = (form: ILeaveSchema) => {
    if (!validateFile(file)) return;
    dispatch(sendLeave({ ...form, periods, attachment: file }));
  };

  const theme = useTheme();

  const leaveState = useSelector(selectLeaveManagementState);

  useEffect(() => {
    dispatch(getLeaveManagement());
  }, [dispatch]);

  const selectPeriodDialog = useDialog();
  const [currentApplyingLeaves, setCurrentApplyingLeaves] = useState<number>(0);

  const handleTotalLeaves = (currentApplyingLeaves: number) => {
    setCurrentApplyingLeaves(currentApplyingLeaves);
  };
  const handleSelectedPeriod = (updatedLeaves: IPeriods[]) => {
    setPeriods(updatedLeaves);
  };
  const leaves = useSelector(selectLeaveState);

  const approvedLeaves = leaves?.data
    ?.filter((option: ILeave) => option.update === 'Approved')
    ?.reduce((totalLeaveDays: number, leave: any) => {
      leave.periods.forEach((period: any) => {
        if (period.period === 'Full Day') {
          totalLeaveDays += 1;
        } else if (period.period.includes('Half')) {
          totalLeaveDays += 0.5;
        }
      });

      return totalLeaveDays;
    }, 0);

  const totalAllowedLeaves =
    leaveState.data
      ?.filter((leaveType) => leaveType.status)
      .reduce((acc, leaveType) => acc + Number(leaveType.allowedDays), 0) ?? 0;

  const hasReachedLeaveLimit = (approvedLeaves as number) + currentApplyingLeaves;
  const isApplyingForLOP = hasReachedLeaveLimit > totalAllowedLeaves;
  const remainingLeaves = Math.max(
    0,
    totalAllowedLeaves - (currentApplyingLeaves + (approvedLeaves as number))
  );

  return (
    <>
      <FormProvider methods={methods}>
        <Dialog open={open} fullWidth>
          <Header
            variant="dialog"
            title={row ? Strings.button.updateLeave : Strings.button.addLeave}
            onClose={handleOnCloseLeavePopup}
          />
          <Divider />
          <Scrollbar>
            <DialogContent>
              <Stack spacing={1} my={2}>
                <RHFTextField
                  name="leaveType"
                  label={Strings.field.type}
                  required
                  variant="outlined"
                  select
                >
                  {leaveState.data?.map(
                    (option) =>
                      option.status && (
                        <MenuItem key={option.id} value={option.leaveType}>
                          {option.leaveType}
                        </MenuItem>
                      )
                  )}
                </RHFTextField>
                <Typography color={theme.palette.grey[500]} fontSize={14} pl={2}>
                  {isApplyingForLOP ? Strings.leave.lossOfPayError : ''}
                </Typography>
                <RHFDatePicker
                  maxDate={selectedDates.to}
                  name="from"
                  label={Strings.field.from}
                  required
                  shouldDisableDate={isWeekend}
                  onChange={(date) => {
                    handleSelectedFromDate(date);
                  }}
                  defaultValue={row ? dayjs(row.from, 'DD/MM/YYYY') : null}
                />
                <RHFDatePicker
                  minDate={selectedDates.from}
                  name="to"
                  label={Strings.field.to}
                  required
                  shouldDisableDate={isWeekend}
                  onChange={(date) => {
                    handleSelectedToDate(date);
                    selectPeriodDialog.show();
                  }}
                  defaultValue={row ? dayjs(row.to, 'DD/MM/YYYY') : null}
                />
                {selectedDates.from && selectedDates.to && (
                  <Stack direction={'row'} pt={2} pb={2} justifyContent={'space-between'}>
                    <Stack direction={'row'} gap={1}>
                      <Stack color={theme.palette.grey[600]}>
                        <Iconify icon="mdi:progress-clock" />
                      </Stack>
                      <Stack direction={'row'} gap={0.5}>
                        <Typography color={theme.palette.grey[500]} fontSize={14}>
                          {Strings.leave.youAreRequesting}
                        </Typography>
                        <Typography fontWeight={700} color={theme.palette.grey[600]} fontSize={15}>
                          {currentApplyingLeaves.toString()} {Strings.field.days}
                        </Typography>
                        {!isApplyingForLOP && (
                          <>
                            <Typography color={theme.palette.grey[500]} fontSize={14}>
                              {Strings.leave.remainingLeaves}
                            </Typography>
                            <Typography
                              fontWeight={700}
                              color={theme.palette.grey[600]}
                              fontSize={15}
                            >
                              {remainingLeaves} {Strings.field.days}
                            </Typography>
                          </>
                        )}
                      </Stack>
                    </Stack>
                    <Stack>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={selectPeriodDialog.show}
                        size="small"
                      >
                        {Strings.button.changePeriod}
                      </Button>
                    </Stack>
                  </Stack>
                )}
                <RHFTextField
                  name="reason"
                  label={Strings.field.reason}
                  required
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={5}
                />
                <RHFFilePickerField
                  name="attachment"
                  label={Strings.field.attachment}
                  accept=".pdf,.png,.jpg,.jpeg"
                />
                {fileError && (
                  <Typography color="error" variant="caption" sx={{ pl: 2 }}>
                    {fileError}
                  </Typography>
                )}
              </Stack>
            </DialogContent>
          </Scrollbar>
          <Divider />
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={handleOnCloseLeavePopup}>
              {Strings.button.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={loading}
              onClick={methods.handleSubmit(onSubmitDraft)}
            >
              {Strings.button.saveInDraft}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={sendLeaves}
              onClick={methods.handleSubmit(onSubmitSend)}
            >
              {Strings.button.sendToManager}
            </LoadingButton>
          </DialogActions>
        </Dialog>
        {selectedDates.from ?? selectedDates.to ? (
          <SelectPeriod
            open={selectPeriodDialog.visible}
            onClose={selectPeriodDialog.hide}
            selectedDates={selectedDates}
            totalLeavesCalculated={handleTotalLeaves}
            handleSelectPeriod={handleSelectedPeriod}
            methods={methods}
          />
        ) : null}
      </FormProvider>
      {notificationDailog.visible && <InformationDialog open onClose={handleCloseNotification} />}
    </>
  );
};

export { AddLeave };
